import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from './DataTable';
import { getQuestions, getQuestion, deleteQuestion, getCategories } from '../../lib/adminApi';

function AdminQuestions() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewQuestion, setViewQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ deleting: false, current: 0, total: 0, failed: [] });
  const [viewTestCases, setViewTestCases] = useState([]);
  const [loadingTestCases, setLoadingTestCases] = useState(false);

  // Ref to prevent double API calls in StrictMode
  const hasFetched = useRef(false);

  const fetchQuestions = async (page = 1, categoryId = selectedCategory) => {
    setLoading(true);
    try {
      const data = await getQuestions(page, pagination.limit, categoryId || null);
      setQuestions(data.questions || []);
      setPagination({ page: data.page, limit: data.limit, total: data.total });
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double fetch in StrictMode
    if (hasFetched.current) return;
    hasFetched.current = true;

    const initializeData = async () => {
      // Always fetch categories first (they may have changed)
      let fetchedCategories = [];
      try {
        const catData = await getCategories();
        fetchedCategories = catData.categories || [];
        setCategories(fetchedCategories);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }

      // Check for cached state for questions
      const cached = sessionStorage.getItem('adminQuestionsState');
      if (cached) {
        try {
          const data = JSON.parse(cached);
          // Validate cached category still exists
          const cachedCategoryExists = !data.selectedCategory ||
            fetchedCategories.some(c => String(c.categoryId) === String(data.selectedCategory));

          if (cachedCategoryExists && data.questions && data.questions.length > 0) {
            setQuestions(data.questions);
            setSelectedCategory(data.selectedCategory || '');
            setPagination(data.pagination);
            setLoading(false);
            return;
          }
        } catch (parseErr) {
          console.error('Failed to parse cached state:', parseErr);
          sessionStorage.removeItem('adminQuestionsState');
        }
      }

      // No valid cache - load first category's questions by default
      try {
        if (fetchedCategories.length > 0) {
          const firstCategoryId = fetchedCategories[0].categoryId;
          setSelectedCategory(String(firstCategoryId));
          await fetchQuestions(1, firstCategoryId);
        } else {
          await fetchQuestions(1, '');
        }
      } catch (err) {
        console.error('Failed to fetch questions:', err);
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Save state when data changes (categories are always fetched fresh, not cached)
  useEffect(() => {
    if (questions.length > 0) {
      sessionStorage.setItem('adminQuestionsState', JSON.stringify({
        questions,
        selectedCategory,
        pagination
      }));
    }
  }, [questions, selectedCategory, pagination]);

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    fetchQuestions(1, categoryId);
  };

  const handleDelete = async (questionId) => {
    try {
      await deleteQuestion(questionId);
      setDeleteConfirm(null);
      sessionStorage.removeItem('adminQuestionsState');
      fetchQuestions(pagination.page, selectedCategory);
    } catch (err) {
      console.error('Failed to delete question:', err);
    }
  };

  const handleViewQuestion = async (row) => {
    setViewQuestion(row);
    setLoadingTestCases(true);
    try {
      const data = await getQuestion(row.questionId);
      if (data.question) {
        setViewQuestion(data.question);
      }
      setViewTestCases(data.testCases || []);
    } catch (err) {
      console.error('Failed to fetch question details:', err);
      setViewTestCases([]);
    } finally {
      setLoadingTestCases(false);
    }
  };

  const handleBulkDelete = async () => {
    const questionIds = Array.from(selectedQuestions);
    const total = questionIds.length;
    const failed = [];

    setBulkDeleteProgress({ deleting: true, current: 0, total, failed: [] });

    for (let i = 0; i < questionIds.length; i++) {
      const questionId = questionIds[i];
      try {
        await deleteQuestion(questionId);
      } catch (err) {
        console.error(`Failed to delete question ${questionId}:`, err);
        failed.push(questionId);
      }
      setBulkDeleteProgress(prev => ({ ...prev, current: i + 1, failed: [...failed] }));
    }

    // Finished deleting
    setBulkDeleteProgress(prev => ({ ...prev, deleting: false }));

    if (failed.length === 0) {
      // All deleted successfully
      setBulkDeleteConfirm(false);
      setSelectedQuestions(new Set());
      sessionStorage.removeItem('adminQuestionsState');
      fetchQuestions(pagination.page, selectedCategory);
      setBulkDeleteProgress({ deleting: false, current: 0, total: 0, failed: [] });
    } else if (failed.length < total) {
      // Some deleted, some failed - refresh and keep failed selected
      setSelectedQuestions(new Set(failed));
      sessionStorage.removeItem('adminQuestionsState');
      fetchQuestions(pagination.page, selectedCategory);
    }
  };

  const columns = [
    { key: 'questionId', label: 'ID' },
    {
      key: 'question',
      label: 'Question',
      render: (val) => (
        <span className="line-clamp-2" title={val}>
          {val?.substring(0, 100)}{val?.length > 100 ? '...' : ''}
        </span>
      ),
    },
    { key: 'categoryName', label: 'Category' },
    {
      key: 'testCaseCount',
      label: 'Test Cases',
      render: (val) => (
        <span className="px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs">
          {val || 0}
        </span>
      ),
    },
  ];

  const actions = (row) => (
    <div className="flex gap-2 justify-end">
      <button
        onClick={() => handleViewQuestion(row)}
        className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
      >
        View
      </button>
      <button
        onClick={() => navigate(`/admin/questions/${row.questionId}/edit`)}
        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
      >
        Edit
      </button>
      <button
        onClick={() => setDeleteConfirm(row)}
        className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
      >
        Delete
      </button>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-white">Questions</h1>
          {selectedQuestions.size > 0 && (
            <button
              onClick={() => setBulkDeleteConfirm(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              Delete Selected ({selectedQuestions.size})
            </button>
          )}
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedCategory}
            onChange={handleCategoryChange}
            className="px-4 py-2 bg-gray-700 text-white border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.categoryId} value={cat.categoryId}>
                {cat.categoryName}
              </option>
            ))}
          </select>
          <button
            onClick={() => navigate('/admin/questions/new')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            + Add Question
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={questions}
        loading={loading}
        pagination={pagination}
        onPageChange={(page) => fetchQuestions(page, selectedCategory)}
        actions={actions}
        emptyMessage="No questions found. Create your first question!"
        selectable={true}
        selectedIds={selectedQuestions}
        onSelectionChange={setSelectedQuestions}
        idKey="questionId"
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Confirm Delete</h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete question #{deleteConfirm.questionId}?
              This will also delete all associated test cases.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.questionId)}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Question Modal */}
      {viewQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Question Details</h3>
            <div className="space-y-4 text-white">
              <div>
                <span className="text-gray-400">ID:</span> {viewQuestion.questionId}
              </div>
              <div>
                <span className="text-gray-400">Category:</span> {viewQuestion.categoryName}
              </div>
              <div>
                <span className="text-gray-400">Question:</span>
                <pre className="whitespace-pre-wrap mt-1 text-sm">{viewQuestion.question}</pre>
              </div>
              <div>
                <span className="text-gray-400">Template:</span>
                <pre className="bg-gray-900 p-2 rounded mt-1 text-sm overflow-x-auto">{viewQuestion.template}</pre>
              </div>
              <div>
                <span className="text-gray-400">Answer:</span>
                <pre className="bg-gray-900 p-2 rounded mt-1 text-sm overflow-x-auto">{viewQuestion.answer}</pre>
              </div>
              {viewQuestion.statements && (
                <div>
                  <span className="text-gray-400">Statements:</span>
                  <pre className="bg-gray-900 p-2 rounded mt-1 text-sm">{viewQuestion.statements}</pre>
                </div>
              )}
              {viewQuestion.connectionScopes && (
                <div>
                  <span className="text-gray-400">Connection Scopes:</span>
                  <pre className="bg-gray-900 p-2 rounded mt-1 text-sm">{viewQuestion.connectionScopes}</pre>
                </div>
              )}
              {/* Test Cases Section */}
              <div className="mt-4 pt-4 border-t border-gray-700">
                <span className="text-gray-400">Test Cases ({viewTestCases.length}):</span>
                {loadingTestCases ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : viewTestCases.length === 0 ? (
                  <p className="text-gray-500 text-sm mt-2">No test cases found</p>
                ) : (
                  <div className="space-y-3 mt-2">
                    {viewTestCases.map((tc, idx) => (
                      <div key={tc.testCaseId} className="bg-gray-900 p-3 rounded border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-blue-400 font-medium text-sm">Test Case #{idx + 1}</span>
                          {tc.hidden && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-600/20 text-yellow-400 rounded">Hidden</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 text-xs">Input:</span>
                            <pre className="text-gray-300 mt-1 whitespace-pre-wrap">{tc.inputValue || '(empty)'}</pre>
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Expected Output:</span>
                            <pre className="text-gray-300 mt-1 whitespace-pre-wrap">{tc.outputValue || '(empty)'}</pre>
                          </div>
                        </div>
                        {tc.explanation && (
                          <div className="mt-2">
                            <span className="text-gray-500 text-xs">Explanation:</span>
                            <p className="text-gray-400 text-sm mt-1">{tc.explanation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                setViewQuestion(null);
                setViewTestCases([]);
              }}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {bulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">
              {bulkDeleteProgress.deleting ? 'Deleting Questions...' : 'Confirm Bulk Delete'}
            </h2>

            {bulkDeleteProgress.deleting ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm text-gray-300">
                  <span>Progress: {bulkDeleteProgress.current} / {bulkDeleteProgress.total}</span>
                  <span>{Math.round((bulkDeleteProgress.current / bulkDeleteProgress.total) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full transition-all duration-300 ease-out"
                    style={{
                      width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                      background: 'linear-gradient(90deg, #ef4444 0%, #f87171 50%, #ef4444 100%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s infinite linear'
                    }}
                  />
                </div>
                <style>{`
                  @keyframes shimmer {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                  }
                `}</style>
                {bulkDeleteProgress.failed.length > 0 && (
                  <p className="text-yellow-400 text-sm">
                    Failed to delete {bulkDeleteProgress.failed.length} question(s)
                  </p>
                )}
              </div>
            ) : bulkDeleteProgress.failed.length > 0 ? (
              <div className="space-y-4">
                <p className="text-yellow-400">
                  Failed to delete {bulkDeleteProgress.failed.length} question(s): {bulkDeleteProgress.failed.join(', ')}
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setBulkDeleteConfirm(false);
                      setBulkDeleteProgress({ deleting: false, current: 0, total: 0, failed: [] });
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Retry Failed ({bulkDeleteProgress.failed.length})
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-gray-300 mb-6">
                  Are you sure you want to delete {selectedQuestions.size} question{selectedQuestions.size > 1 ? 's' : ''}?
                  This will also delete all associated test cases.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setBulkDeleteConfirm(false)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete {selectedQuestions.size} Question{selectedQuestions.size > 1 ? 's' : ''}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminQuestions;
